/**
 * Logo singleton service.
 *
 * Fetches /api/logo exactly ONCE per page session regardless of how many
 * components call fetchLogoOnce(). Subsequent callers receive the same
 * resolved Promise (in-memory) or the sessionStorage-cached value.
 *
 * Eliminates duplicate /api/logo calls from:
 *   App.jsx, Logo.js, Auth.jsx, ChatTongole.js, SplashLoader.jsx
 */

import { getCached, setCached, TTL } from './apiCache';

// Singleton in-flight / resolved promise
let _promise = null;

/**
 * Returns Promise<logoObject> with all fields:
 *   big_logo, mobile_logo, loader_logo, logo, favicon
 */
export function fetchLogoOnce() {
  if (_promise) return _promise;

  // Warm from sessionStorage so it survives SPA navigation
  const cached = getCached('logo');
  if (cached) {
    _promise = Promise.resolve(cached);
    return _promise;
  }

  _promise = fetch(`${process.env.REACT_APP_API_URL}/api/logo`)
    .then(res => {
      if (!res.ok) throw new Error('logo fetch failed');
      return res.json();
    })
    .then(data => {
      const item = Array.isArray(data) ? data[0] : data;
      setCached('logo', item, TTL.logo);
      return item;
    })
    .catch(err => {
      _promise = null; // reset so subsequent mounts can retry
      console.error('[logoService]', err);
      return {};
    });

  return _promise;
}
